"use client";

import React from "react";
import { FileText, ArrowRight, ShieldCheck, CircleCheck, SlidersHorizontal } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
import Reveal from "@/components/common/Reveal";
import { useLanguage } from "@/hooks/useLanguage";
import { pick } from "@/lib/i18n";
import { COMMON } from "@/lib/commonCopy";
export default function ResourcesIntro() {
  const {
    lang
  } = useLanguage();
  const eyebrow = pick(lang, {
    en: "Engineering & Specification Portal",
    zh: "工程与技术规格中心",
    es: "Portal de ingeniería y especificaciones",
    de: "Engineering- & Spezifikationsportal",
    fr: "Portail d'ingénierie et de spécifications",
    pt: "Portal de engenharia e especificações"
  });
  const heading = pick(lang, {
    en: "Technical Resources",
    zh: "技术资源中心",
    es: "Recursos técnicos",
    de: "Technische Ressourcen",
    fr: "Ressources techniques",
    pt: "Recursos técnicos"
  });
  const lead = pick(lang, {
    en: "The central engineering reference library for industrial buyers and filtration designers specifying stainless steel wire mesh and sintered media. Access comprehensive aperture tolerance charts, open area formulas, and differential pressure models across square and Dutch weaves.",
    zh: "面向工业采购与过滤工程师的权威技术资料库，汇集不锈钢编织丝网与烧结滤材的工程选型工具、国际执行标准及计算模型。提供从方孔网到微米级密纹滤布的孔径公差、开孔率公式与流体力学压降参考数据。",
    es: "Biblioteca de referencia de ingeniería para compradores industriales y diseñadores de filtración que especifican malla de alambre de acero inoxidable y medios sinterizados. Acceda a tablas completas de tolerancia de abertura, fórmulas de área abierta y modelos de presión diferencial en tejidos cuadrados y holandeses.",
    de: "Zentrale Engineering-Referenzbibliothek für industrielle Einkäufer und Filtrationskonstrukteure von Edelstahldrahtgewebe und Sintermedien. Greifen Sie auf Maschenweitentoleranztabellen, Formeln für offene Siebflächen und Differenzdruckmodelle für Quadrat- und Tressengewebe zu.",
    fr: "Bibliothèque technique de référence pour acheteurs industriels et concepteurs de filtration prescrivant la toile métallique en acier inoxydable et les médias frittés. Accédez aux tables de tolérances d'ouverture, formules de surface ouverte et modèles de perte de charge pour toiles carrées et tissages hollandais.",
    pt: "Biblioteca central de referência de engenharia para compradores industriais e projetistas de filtração que especificam malha de arame de aço inoxidável e meios sinterizados. Acesse tabelas completas de tolerância de abertura, fórmulas de área aberta e modelos de perda de carga em malhas quadradas e holandesas."
  });
  const viewDatasheets = pick(lang, {
    en: "View Datasheets",
    zh: "查阅技术数据表",
    es: "Ver fichas técnicas",
    de: "Datenblätter anzeigen",
    fr: "Consulter les fiches techniques",
    pt: "Ver fichas técnicas"
  });
  const overlayChip = pick(lang, {
    en: "Engineering Reference Suite",
    zh: "工程参考资源",
    es: "Paquete de referencia técnica",
    de: "Technische Referenzsammlung",
    fr: "Suite de référence technique",
    pt: "Pacote de referência técnica"
  });
  const consultationNotice = pick(lang, {
    en: "Technical consultation from engineering",
    zh: "专业技术工程师选型咨询",
    es: "Consulta técnica con ingenieros especialistas",
    de: "Technische Beratung durch Fachingenieure",
    fr: "Conseil technique par notre équipe d'ingénierie",
    pt: "Consultoria técnica com engenheiros especializados"
  });
  const contactTeam = pick(lang, {
    en: "Contact Team",
    zh: "联系工程团队",
    es: "Contactar al equipo",
    de: "Team kontaktieren",
    fr: "Contacter l'équipe",
    pt: "Contatar equipe"
  });
  const specStripItems = [{
    label: pick(lang, {
      en: "Square Weave Range",
      zh: "方孔网目数范围",
      es: "Rango de tejido cuadrado",
      de: "Quadratmaschen-Bereich",
      fr: "Plage toiles carrées",
      pt: "Faixa de malha quadrada"
    }),
    value: pick(lang, {
      en: "1 – 635 mesh",
      zh: "1 – 635 目",
      es: "1 – 635 mallas",
      de: "1 – 635 Mesh",
      fr: "1 – 635 mailles",
      pt: "1 – 635 mesh"
    })
  }, {
    label: pick(lang, {
      en: "Wire Diameter",
      zh: "线径规格范围",
      es: "Diámetro de alambre",
      de: "Drahtdurchmesser",
      fr: "Diamètre de fil",
      pt: "Diâmetro do arame"
    }),
    value: "0.020 – 2.0 mm"
  }, {
    label: pick(lang, {
      en: "Combined Woven Range",
      zh: "编织过滤精度范围",
      es: "Rango de tejido combinado",
      de: "Kombinierter Gewebebereich",
      fr: "Plage tissée combinée",
      pt: "Faixa de tecido combinada"
    }),
    value: "1 µm – 25 mm"
  }, {
    label: pick(lang, {
      en: "Reference Standards",
      zh: "制造与检验标准",
      es: "Normas de referencia",
      de: "Referenznormen",
      fr: "Normes de référence",
      pt: "Normas de referência"
    }),
    value: "ISO 9044 / ASTM E2016 / E2814"
  }];
  return <section id="technicalresourcesintro" data-nav="light" className="relative overflow-hidden bg-background mf-hero border-b border-border text-foreground">
      <div className="mf-container relative">
        <div className="grid grid-cols-1 items-center gap-8 lg:grid-cols-12">
          {/* Left Column (7 Columns on lg) */}
          <Reveal className="lg:col-span-7 flex flex-col items-start">
            {/* Badges: exactly two outline badges */}
            <div className="mb-4 flex flex-wrap items-center gap-2">
              <Badge variant="outline" className="gap-1.5 rounded-md border-border bg-card px-3 py-1 font-mono mf-caption text-foreground">
                <SlidersHorizontal className="size-3.5 text-primary" aria-hidden="true" />
                <span>{eyebrow}</span>
              </Badge>
              <Badge variant="outline" className="gap-1.5 rounded-md border-border bg-muted px-3 py-1 font-mono mf-caption text-foreground">
                <ShieldCheck className="size-3.5 text-primary" aria-hidden="true" />
                <span>ISO 9044 / ASTM E2016</span>
              </Badge>
            </div>

            {/* H1 Headline */}
            <h1 className="font-default text-balance mf-h1 text-foreground">
              {heading}
            </h1>

            {/* 2-3 sentence lead paragraph */}
            <p className="mt-4 max-w-2xl text-muted-foreground mf-lead">
              {lead}
            </p>

            {/* 2 CTAs */}
            <div className="mt-6 flex w-full flex-col gap-3 sm:w-auto sm:flex-row sm:items-center">
              <Button size="lg" asChild className="text-sm font-semibold transition-colors">
                <Link to="/RequestAQuotePage" className="inline-flex items-center gap-2">
                  <span>{pick(lang, COMMON.requestQuote)}</span>
                  <ArrowRight className="size-4" aria-hidden="true" />
                </Link>
              </Button>

              <Button variant="outline" size="lg" asChild className="text-sm font-semibold transition-colors">
                <Link to="/DatasheetsPage" className="inline-flex items-center gap-2">
                  <FileText className="size-4" aria-hidden="true" />
                  <span>{viewDatasheets}</span>
                </Link>
              </Button>
            </div>
          </Reveal>

          {/* Right Column: Hero Visual Card (5 Columns on lg) */}
          <Reveal delay={120} className="lg:col-span-5 w-full">
            <Card className="overflow-hidden rounded-lg border border-border bg-card gap-0 py-0">
              <div className="relative mf-card-media mf-ratio-hero w-full overflow-hidden">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/clean-documentary-photograph-of-a-techni-a36e5d.png" alt={pick(lang, {
                en: "Precision stainless steel wire mesh aperture inspection under optical microscope",
                zh: "光学显微镜下精密不锈钢丝网孔径公差检测",
                es: "Inspección de abertura de malla de alambre de acero inoxidable bajo microscopio óptico",
                de: "Prüfung von Edelstahldrahtgewebe-Maschenweiten unter dem optischen Mikroskop",
                fr: "Contrôle d'ouverture de toile métallique en acier inoxydable au microscope optique",
                pt: "Inspeção de abertura de malha de arame de aço inoxidável sob microscópio óptico"
              })} loading="eager" fetchPriority="high" decoding="async" className="h-full w-full object-cover" />
                <div className="absolute top-3 left-3 bg-card border border-border px-3 py-1 rounded-md mf-caption font-mono flex items-center gap-1.5 text-foreground">
                  <SlidersHorizontal className="size-3.5 text-primary" aria-hidden="true" />
                  <span>{overlayChip}</span>
                </div>
              </div>

              {/* Single Footer Row */}
              <CardContent className="p-4">
                <div className="flex items-center justify-between mf-caption">
                  <div className="flex items-center gap-1.5 text-muted-foreground">
                    <CircleCheck className="size-3.5 text-primary" aria-hidden="true" />
                    <span>{consultationNotice}</span>
                  </div>
                  <Link to="/ContactPage" className="font-medium text-primary hover:underline whitespace-nowrap ml-2 flex items-center gap-1 shrink-0">
                    <span>{contactTeam}</span>
                    <ArrowRight className="size-3.5" aria-hidden="true" />
                  </Link>
                </div>
              </CardContent>
            </Card>
          </Reveal>
        </div>

        {/* Spec Strip below grid */}
        <div className="mf-spec-strip">
          {specStripItems.map((spec, idx) => <div key={idx} className="mf-spec-strip__item">
              <span className="mf-spec-strip__label">{spec.label}</span>
              <span className="mf-spec-strip__value">{spec.value}</span>
            </div>)}
        </div>
      </div>
    </section>;
}